import { useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";
import Table from "react-bootstrap/Table";
import "./vendorlisting.scss";
import threedotvertical from "../../../assets/images/three-dots-vertical.svg";
import editicon from "../../../assets/images/edit-icon.svg";
import viewicon from "../../../assets/images/view-icon.svg";
import deactivateicon from "../../../assets/images/deactivate-icon.svg";
import duplicateicon from "../../../assets/images/duplicate-icon.svg";
import deleteicon from "../../../assets/images/delete-icon.svg";
import ListingTitle from "../../../components/listingTitle/ListingTitle";
import { ReactSVG } from "react-svg";
import useVendors from "../../../Hooks/useVendors";
import Loading from "../../../components/LoadingPage/Loading";
import { Vendor, setSelectedVendor } from "../../../redux/vendor/vendorSlice";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { selectCurrentUserId } from "../../../redux/auth/authSlice";
import { toggleClass } from "../../../utils/commonUtils";
import { DeleteConfModel } from "../../../components/DeleteConfModel/deleteConfModel";
import ItemListingTitle from "../../../components/itemListingTitle/itemListingTitle";
import { CommonDuplicateModel } from "../../../components/commonDuplicateModel/duplicateModel";
import { useModulePermissions, PermissionIdentifier, PermissionModule } from "../../../utils/permissionUtils";

const VendorListing = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentUserId = useAppSelector(selectCurrentUserId);
  const [showModal, setShowModal] = useState(false);
  const [vendorsList, setVendorsList] = useState<Vendor[]>([]);
  const [currentVendor, setCurrentVendor] = useState<Vendor>();
  const defaultFilter = { searchText: "", active: true };
  const [searchFieldVal, setSearchFieldVal] = useState(defaultFilter);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const {
    getVendorsAPI,
    getVendorsLoading,
    activeVendorAPI,
    setVendorStatusActiveLoading,
    deactiveVendorAPI,
    setVendorStatusDeActiveLoading,
    deleteVendorAPI,
    deleteVendorLoading,
    duplicateVendorAPI,
    duplicateVendorLoading
  } = useVendors();

  const deleteVendor = async () => {
    await deleteVendorAPI({
      id: currentVendor?.id,
      updatedBy: currentUserId ?? "",
    });
    toggleModal();
    getVendors();
  };

  const toggleModal = () => {
    toggleClass();
    setShowModal(!showModal);
  };

  const getVendors = () => {
    getVendorsAPI(searchFieldVal)
      .then((res: Vendor[]) => setVendorsList(res))
      .catch();
  };

  const editVendor = (vendor: Vendor) => {
    dispatch(setSelectedVendor(vendor));
    navigate("/edit-vendor");
  };

  const navigateToView = (vendor: Vendor) => {
    dispatch(setSelectedVendor(vendor));
    navigate("/view-vendor");
  };

  const updateStatus = async (vendor: Vendor) => {
    if (vendor.isActive) {
      await deactiveVendorAPI({
        id: vendor.id,
        updatedBy: currentUserId ?? "",
      });
    } else {
      await activeVendorAPI({ id: vendor.id, updatedBy: currentUserId ?? "" });
    }
    getVendors();
  };

  const toggleDuplicateModal = () => {
    toggleClass();
    setShowDuplicateModal(!showDuplicateModal);
  };

  const duplicateVendor = async () => {
    await duplicateVendorAPI({
      id: currentVendor?.id,
      updatedBy: currentUserId ?? "",
    });
    toggleDuplicateModal();
    getVendors();
  };

  useEffect(() => {
    getVendors();
  }, [searchFieldVal]);

  const isPermitted = useModulePermissions(PermissionModule.VENDER_MASTER);
  const isViewPermitted = isPermitted(PermissionIdentifier.VIEW_VENDOR_MASTER);
  const isCreatePermitted = isPermitted(PermissionIdentifier.CREATE_VENDOR_MASTER);
  const isEditPermitted = isPermitted(PermissionIdentifier.EDIT_VENDOR_MASTER);
  const isDeletePermitted = isPermitted(PermissionIdentifier.DELETE_VENDOR_MASTER);
  const isDuplicatePermitted = isPermitted(PermissionIdentifier.DUPLICATE_VENDOR_MASTER);
  const isInactivePermitted = isPermitted(PermissionIdentifier.INACTIVE_VENDOR_MASTER);
  const isAnyActionPermitted = isViewPermitted || isEditPermitted || isDeletePermitted || isDuplicatePermitted || isInactivePermitted;

  return (
    <div className="vendor-listing-wrapper">
      {getVendorsLoading ||
        deleteVendorLoading ||
        setVendorStatusActiveLoading ||
        setVendorStatusDeActiveLoading ||
        duplicateVendorLoading ? (
        <Loading />
      ) : null}

      <ItemListingTitle
        title="Vendor Master"
        buttonTitle="Add New"
        href="/add-vendor"
        disRecords={vendorsList.length || 0}
        totalRecords={vendorsList.length || 0}
        searchVal={searchFieldVal}
        setSearchVal={setSearchFieldVal}
        defaultFilter={defaultFilter}
        isAddPermitted={isCreatePermitted}
      />

      <div className="listing-table-wrapper">
        <Table responsive="md" hover={true}>
          <thead>
            <tr>
              <th>Name</th>
              <th>City</th>
              <th>State</th>
              <th>Zip</th>
              <th>Email Id</th>
              <th>Phone</th>
              <th>Contact Person</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {vendorsList?.map((vendor: Vendor) => (
              <tr
                key={vendor.id}
                onDoubleClick={() => {
                  navigateToView(vendor);
                }}
              >
                <td title={vendor.name}>{vendor.name}</td>
                <td>{vendor.city}</td>
                <td>{vendor.state}</td>
                <td>{vendor.zipCode}</td>
                <td title={vendor.email}>{vendor.email}</td>
                <td title={vendor.phone}>{vendor.phone}</td>
                <td title={vendor.contactPerson}>{vendor.contactPerson}</td>
                <td>
                  <span
                    className={`status-circle ${vendor.isActive ? "active" : "inactive"}`}
                  >
                    {vendor.isActive ? "Active" : "Inactive"}
                  </span>
                </td>
                <td>
                  <Dropdown>
                    {isAnyActionPermitted && (
                      <Dropdown.Toggle variant="link" id="dropdown-basic">
                        <ReactSVG
                          className="svg-box"
                          wrapper="span"
                          src={threedotvertical}
                        />
                      </Dropdown.Toggle>
                    )}
                    <Dropdown.Menu>
                      {isViewPermitted && (
                        <Dropdown.Item onClick={() => navigateToView(vendor)}>
                          <ReactSVG
                            className="svg-box"
                            wrapper="span"
                            src={viewicon}
                          />
                          View
                        </Dropdown.Item>
                      )}
                      {isInactivePermitted && (
                        <Dropdown.Item onClick={() => updateStatus(vendor)}>
                          <ReactSVG
                            className="svg-box"
                            wrapper="span"
                            src={deactivateicon}
                          />
                          {vendor.isActive ? "Deactivate" : "Activate"}
                        </Dropdown.Item>
                      )}
                      {isEditPermitted && (
                        <Dropdown.Item onClick={() => editVendor(vendor)}>
                          <ReactSVG
                            className="svg-box"
                            wrapper="span"
                            src={editicon}
                          />
                          Edit
                        </Dropdown.Item>
                      )}
                      {isDeletePermitted && (
                        <Dropdown.Item
                          onClick={() => {
                            setCurrentVendor(vendor);
                            toggleModal();
                          }}
                        >
                          <ReactSVG
                            className="svg-box"
                            wrapper="span"
                            src={deleteicon}
                          />
                          Delete
                        </Dropdown.Item>
                      )}
                      {isDuplicatePermitted && (
                        <Dropdown.Item
                          onClick={() => {
                            setCurrentVendor(vendor);
                            toggleDuplicateModal();
                          }}
                        >
                          <ReactSVG
                            className="svg-box"
                            wrapper="span"
                            src={duplicateicon}
                          />
                          Duplicate
                        </Dropdown.Item>
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <DeleteConfModel
        showModal={showModal}
        toggleModal={toggleModal}
        deleteAction={deleteVendor}
        recordName={currentVendor?.name || ""}
        modelName={`vendor`}
        isDeletePermitted={isDeletePermitted}
      />

      <CommonDuplicateModel
        showDuplicateModal={showDuplicateModal}
        toggleDuplicateModal={toggleDuplicateModal}
        modelName={`vendor`}
        recordName={currentVendor?.name || ""}
        duplicate={duplicateVendor}
        isDuplicatePermitted={isDuplicatePermitted}
      />
    </div>
  );
};

export default VendorListing;
